import { HttpClient } from './httpClient'
import { API_ENDPOINTS } from '../../config/config'

export interface RfidCard {
  _id: string
  cardNumber: string
  userNumber: string
  userName: string
  enabled: boolean
  date: string
  lastUsed?: string | null
  owner?: {
    _id: string
    username?: string
    email?: string
  }
}

export interface BulkUploadResult {
  success: number
  errors: Array<{ row: number; error: string; data?: any }>
  duplicates: Array<{ row: number; cardNumber: string }>
}

export interface RfidHistoryEntry {
  _id: string
  device: {
    _id: string
    name: string
    app: string
    city: string
  }
  type: string
  action: string
  value: string
  date: number
  topic: string
  parsedData?: {
    status: 'granted' | 'denied' | 'error'
    cardNumber: string
    userNumber?: string
    userName?: string
    channel?: string
    reason?: string
    error?: string
    timestamp: number
  }
}

export interface RfidHistoryResponse {
  data: RfidHistoryEntry[]
  meta: {
    total: number
    limit: number
    skip: number
  }
}

export class RfidCardService {
  /**
   * Get RFID cards for the authenticated user
   */
  static async getMyCards(): Promise<RfidCard[]> {
    return HttpClient.get<RfidCard[]>(
      `${API_ENDPOINTS.BASE}/rfid-cards/my-cards`,
    )
  }

  /**
   * Get all RFID cards in the system (admin only)
   */
  static async getAllCards(): Promise<RfidCard[]> {
    return HttpClient.get<RfidCard[]>(
      `${API_ENDPOINTS.BASE}/rfid-cards`,
    )
  }

  /**
   * Get RFID cards for a specific user (admin can access any user)
   */
  static async getCards(userId: string): Promise<RfidCard[]> {
    return HttpClient.get<RfidCard[]>(
      `${API_ENDPOINTS.BASE}/users/${userId}/rfid-cards`,
    )
  }

  /**
   * Add a new RFID card to a user
   */
  static async addCard(
    userId: string,
    card: {
      cardNumber: string
      userNumber: string
      userName: string
      enabled?: boolean
      date?: string
    },
  ): Promise<RfidCard> {
    return HttpClient.post<RfidCard>(
      `${API_ENDPOINTS.BASE}/users/${userId}/rfid-cards`,
      card,
    )
  }

  /**
   * Bulk upload RFID cards from CSV/Excel file
   */
  static async bulkUpload(
    userId: string,
    file: File,
  ): Promise<BulkUploadResult> {
    const formData = new FormData()
    formData.append('file', file)

    return HttpClient.post<BulkUploadResult>(
      `${API_ENDPOINTS.BASE}/users/${userId}/rfid-cards/bulk`,
      formData,
    )
  }

  /**
   * Update an RFID card
   */
  static async updateCard(
    userId: string,
    cardId: string,
    updates: {
      userNumber?: string
      userName?: string
      enabled?: boolean
      date?: string
    },
  ): Promise<RfidCard> {
    return HttpClient.put<RfidCard>(
      `${API_ENDPOINTS.BASE}/users/${userId}/rfid-cards/${cardId}`,
      updates,
    )
  }

  /**
   * Delete an RFID card
   */
  static async deleteCard(userId: string, cardId: string): Promise<void> {
    return HttpClient.delete<void>(
      `${API_ENDPOINTS.BASE}/users/${userId}/rfid-cards/${cardId}`,
    )
  }

  /**
   * Toggle enabled/disabled status of a card
   */
  static async toggleCard(userId: string, cardId: string): Promise<RfidCard> {
    return HttpClient.patch<RfidCard>(
      `${API_ENDPOINTS.BASE}/users/${userId}/rfid-cards/${cardId}/toggle`,
      {},
    )
  }

  /**
   * Download CSV template
   */
  static downloadTemplate(): void {
    const csvContent = [
      'fecha,numeroserie,numeroimpreso,nombreusuario,estado',
      '2026-02-05 08:54:46,0165CA1200660045,001,Enrique,Habilitado',
      '2026-02-05 08:55:02,0194741300660062,002,Jose Ramón,Deshabilitado',
    ].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute('download', 'plantilla_tarjetas_rfid.csv')
    link.style.visibility = 'hidden'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  /**
   * Get RFID access history for a user
   */
  static async getRfidHistory(
    userId: string,
    options: { limit?: number; skip?: number } = {},
  ): Promise<RfidHistoryResponse> {
    const { limit = 100, skip = 0 } = options
    return HttpClient.get<RfidHistoryResponse>(
      `${API_ENDPOINTS.BASE}/users/${userId}/rfid/history?limit=${limit}&skip=${skip}`,
    )
  }
}
